import * as React from 'react'
import styled from 'styled-components'
import { useDebouncedCallback } from 'use-debounce'
import { useDispatch, useSelector } from 'react-redux'
import { IconChat } from '@loadsmart/icons'

import { Currency, Quantity } from 'components/Input'
import { useInput } from 'hooks/useInput'
import {
  clearLane,
  deleteLane,
  submitLane,
  updateLane,
} from 'features/carrierProposal/actions'
import { Tr, Td } from 'components/Table'
import Equipment from 'components/Equipment'
import Tooltip from 'components/Tooltip'
import { formatAddress } from 'utils/address'
import { laneErrorsSelector } from 'features/carrierProposal/selectors'

const Address = styled.b`
  font-weight: 700;
  font-size: 14px;
  line-height: 18px;
}
`

function getInputStatus(meta: LaneMeta) {
  if (!meta.submitted) return 'locked'
}

function parseRate(rate: string | number) {
  return rate.toString().replace(/[^\d.]/g, '')
}

function getSubmitFunction(submitted: boolean, ratePrice: number) {
  if (ratePrice > 0) {
    if (submitted) {
      return updateLane
    }
    return submitLane
  }

  if (submitted) {
    return deleteLane
  }
  return clearLane
}

interface Props extends Lane {
  isLocked: boolean
}

export default function Lane({
  id,
  origin_city,
  origin_state,
  origin_zip,
  dest_city,
  dest_state,
  dest_zip,
  equipment_type,
  mileage,
  volume,
  notes,
  isLocked,
  lane_proposal,
  display_id,
  meta,
}: Props) {
  const dispatch = useDispatch()
  const laneErrors = useSelector(laneErrorsSelector)
  const { value: rateValue, onChange: rateOnChange } = useInput(
    // If the rate is 0 we should show as empty
    Number(lane_proposal?.rate_price) || undefined
  )
  const { value: capacityValue, onChange: capacityOnChange } = useInput(
    lane_proposal?.capacity !== undefined ? lane_proposal?.capacity : volume
  )
  const [handleBlur] = useDebouncedCallback(() => {
    const capacity = Number(capacityValue)
    const rate_price = Number(parseRate(rateValue))
    const submitFunction = getSubmitFunction(meta.submitted, rate_price)
    dispatch(
      submitFunction({
        id: lane_proposal.id,
        awarded_type: lane_proposal.awarded_type,
        awarded_volume: lane_proposal.awarded_volume,
        lane: id,
        capacity,
        rate_price,
      })
    )
  }, 750)
  const inputStatus = getInputStatus(meta)
  const hasError = !!(
    laneErrors &&
    laneErrors[id] &&
    (laneErrors[id].capacity || laneErrors[id].rate_price)
  )
  const rateError = hasError && laneErrors[id].rate_price
  const capacityError = hasError && laneErrors[id].capacity

  return (
    <Tr hasError={hasError}>
      <Td>
        <Address>
          {formatAddress(origin_city, origin_state, origin_zip)}
        </Address>
      </Td>
      <Td>
        <Address>{formatAddress(dest_city, dest_state, dest_zip)}</Address>
      </Td>
      <Td>{display_id}</Td>
      <Td className="text-center">{mileage}</Td>
      <Td className="text-center">{volume}</Td>
      <Td className="iconField">
        <Equipment equipmentType={equipment_type} />
      </Td>
      <Td>
        {notes ? <IconChat data-tip={notes} width={18} height={18} /> : ''}
      </Td>
      <Td className="inputField">
        <Currency
          aria-label="rate"
          data-tip
          data-for={`laneRateError-${id}`}
          disabled={isLocked || meta.isSubmitting}
          error={rateError || ''}
          name="rate"
          onBlur={handleBlur}
          onChange={rateOnChange}
          status={inputStatus}
          value={rateValue}
        />
        {rateError && <Tooltip id={`laneRateError-${id}`}>{rateError}</Tooltip>}
      </Td>
      <Td>
        <Quantity
          aria-label="capacity"
          data-tip
          data-for={`laneCapacityError-${id}`}
          disabled={isLocked || meta.isSubmitting}
          error={capacityError || ''}
          name="capacity"
          onBlur={handleBlur}
          onChange={capacityOnChange}
          status={inputStatus}
          value={capacityValue}
        />
        {capacityError && (
          <Tooltip id={`laneCapacityError-${id}`}>{capacityError}</Tooltip>
        )}
      </Td>
    </Tr>
  )
}
